/**
 * Admin sign-in, the parts with no browser storage and no Vite globals (docs/sign-in.md; ported from the Hype Forge
 * Base apps/admin/src/lib/signIn.ts, 2026-10-22), so node:test loads them as they are
 * (apps/admin/test/sign-in-wait.test.mjs, apps/admin/test/sign-in-turnstile.test.mjs): the two calls to the API, the
 * two login steps with their Turnstile token (which action, when the widget is reset), how a limited answer (HTTP 429)
 * becomes the wait the login page shows, and which of the login page's buttons a wait holds. lib/auth.ts wraps the
 * calls with the API's origin and the session storage; auth/LoginPage.tsx renders the holds.
 */

// ── The words for a wait. The API words its 429 answers with the same functions (apps/api/src/lib/sign-in-wait.ts);
// apps/admin/test/sign-in-wait.test.mjs checks the two copies agree. ──

/** "45 seconds", "1 minute", "12 minutes", "3 hours": a Retry-After (seconds) rounded UP, so nobody is told too early. */
export function signInWaitText(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(1, Math.ceil(seconds)) : 60;
  if (s < 60) return s === 1 ? '1 second' : `${s} seconds`;
  if (s < 90 * 60) {
    const minutes = Math.ceil(s / 60);
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  const hours = Math.ceil(s / 3600);
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/** The whole sentence a limited sign-in shows: "Too many sign-in attempts. Try again in 12 minutes." */
export function signInWaitMessage(seconds: number): string {
  return `Too many sign-in attempts. Try again in ${signInWaitText(seconds)}.`;
}

/** Seconds out of a 429 answer: its JSON `retry_after`, else the Retry-After header, else a minute. */
export function retryAfterFrom(body: unknown, header: string | null | undefined): number {
  const fromBody =
    body && typeof body === 'object' ? Number((body as { retry_after?: unknown }).retry_after) : NaN;
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(header);
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return 60;
}

// ── The calls ──

/** Digits in the emailed sign-in code when the API does not say (it does: `code_length`). */
export const DEFAULT_CODE_LENGTH = 8;

/** The Turnstile action of the email step (STAFF_LOGIN_ACTION in apps/api/src/routes/auth-magic.ts). */
export const STAFF_LOGIN_ACTION = 'staff_login';
/** The Turnstile action of the code step (STAFF_VERIFY_ACTION in apps/api/src/routes/auth-magic.ts). */
export const STAFF_VERIFY_ACTION = 'staff_verify';

/**
 * What the login steps need of the Turnstile widget (lib/turnstile-handle.ts's handle; null when the site has Turnstile
 * off or Cloudflare's script could not load, and the request then goes without a token for the API to decide).
 */
export interface LoginTurnstile {
  /** Run the widget for this action (a token is minted for one action); a no-op when it already does. */
  use(action: string): void;
  /** Wait for the current token ('' when none came). */
  token(ms?: number): Promise<string>;
  /** A token is single use: start a fresh challenge for the next request. */
  reset(): void;
}

/** What the code step says when the API refuses a code (it sends its own words; this is the fallback). */
export const CODE_REFUSED = "That code didn't work. Check it and try again, or request a new one.";

/**
 * What the code step says when the Turnstile check fails (403 turnstile_failed). Not the API's words, which tell the
 * email step to reload: a reload would drop the code step, and the only way back would be a new code (one of the
 * address's requests, and the typed code retired). The check cost no try, so the typed code stays for another press.
 */
export const CODE_STEP_TURNSTILE_RETRY = "We couldn't confirm this came from a person. Press Sign in to try again.";

/** A user as the API's verify answer carries it. */
export interface SignInUser {
  id: string;
  email: string;
  role: 'admin' | 'editor';
}

/** What a successful verify hands back, for lib/auth.ts to store. */
export interface SignInSession {
  access_token: string;
  expires_at: string;
  user: SignInUser;
}

/**
 * A limited answer (429): the seconds to wait (its JSON `retry_after`, else the Retry-After header, else a minute)
 * and the sentence for the page ("Too many sign-in attempts. Try again in 12 minutes."). null for any other answer.
 */
export function limitedWait(
  status: number,
  body: unknown,
  retryAfterHeader: string | null,
): { error: string; retryAfter: number } | null {
  if (status !== 429) return null;
  const retryAfter = retryAfterFrom(body, retryAfterHeader);
  return { error: signInWaitMessage(retryAfter), retryAfter };
}

async function postJson(
  fetchImpl: typeof fetch,
  url: string,
  payload: unknown,
): Promise<{ res: Response; body: unknown }> {
  const res = await fetchImpl(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body: unknown = await res.json().catch(() => null);
  return { res, body };
}

/**
 * Ask for a sign-in email (an 8-digit code plus a link). `website` is the login form's honeypot field (empty for
 * people). `turnstileToken` is the Turnstile widget's token, sent once the site has Turnstile on. The API says how many
 * digits the code has. A 429 comes back as the wait (`retryAfter`, seconds) with its sentence as `error`.
 */
export async function requestSignInCode(
  apiBase: string,
  email: string,
  website = '',
  turnstileToken?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ error: string | null; codeLength: number; retryAfter?: number }> {
  try {
    const { res, body } = await postJson(fetchImpl, `${apiBase}/api/auth/request-link`, {
      email,
      website,
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    });
    const wait = limitedWait(res.status, body, res.headers.get('Retry-After'));
    if (wait) return { error: wait.error, codeLength: DEFAULT_CODE_LENGTH, retryAfter: wait.retryAfter };
    const answer = (body ?? null) as { error?: string; code_length?: unknown } | null;
    if (!res.ok) {
      return { error: answer?.error ?? `Request failed (${res.status})`, codeLength: DEFAULT_CODE_LENGTH };
    }
    const n = answer?.code_length;
    return {
      error: null,
      codeLength: typeof n === 'number' && n >= 4 && n <= 12 ? n : DEFAULT_CODE_LENGTH,
    };
  } catch (e) {
    return { error: (e as Error).message, codeLength: DEFAULT_CODE_LENGTH };
  }
}

/**
 * Trade the emailed link's token, or the email and its code (with the Turnstile token once the site has it on), for a
 * session. One path for both. A 429 comes back as the wait; a refusal as the API's own words, else `fallback` with the
 * status; a failed Turnstile check (403 turnstile_failed) is also flagged, so the page can keep the typed code.
 */
export async function redeemSignIn(
  apiBase: string,
  payload: { token: string } | { email: string; code: string; turnstile_token?: string },
  fallback: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ error: string | null; retryAfter?: number; turnstileFailed?: true; session?: SignInSession }> {
  try {
    const { res, body } = await postJson(fetchImpl, `${apiBase}/api/auth/verify`, payload);
    const wait = limitedWait(res.status, body, res.headers.get('Retry-After'));
    if (wait) return { error: wait.error, retryAfter: wait.retryAfter };
    const answer = (body ?? null) as {
      token?: string;
      expires_at?: string;
      user?: SignInUser;
      error?: string;
      code?: string;
    } | null;
    if (!res.ok || !answer?.token || !answer.user || !answer.expires_at) {
      const error = answer?.error ?? `${fallback} (${res.status})`;
      return res.status === 403 && answer?.code === 'turnstile_failed' ? { error, turnstileFailed: true } : { error };
    }
    return {
      error: null,
      session: { access_token: answer.token, expires_at: answer.expires_at, user: answer.user },
    };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

// ── The two login steps with their Turnstile token ──

/**
 * The email step: the widget runs the email step's action (it may still run the code step's, for "send a new code"),
 * its token goes with the request, and then the widget is readied for what comes next: switched to the code step's
 * action once a code is on its way (a fresh widget, so a fresh token), else reset for another try.
 */
export async function sendSignInCode(
  apiBase: string,
  email: string,
  website: string,
  turnstile: LoginTurnstile | null,
  fetchImpl: typeof fetch = fetch,
): Promise<{ error: string | null; codeLength: number; retryAfter?: number }> {
  turnstile?.use(STAFF_LOGIN_ACTION);
  const token = turnstile ? await turnstile.token() : '';
  const result = await requestSignInCode(apiBase, email, website, token || undefined, fetchImpl);
  if (result.error === null) turnstile?.use(STAFF_VERIFY_ACTION);
  else turnstile?.reset();
  return result;
}

/**
 * The code step: a token for the code step's action goes with the email and the code, and the widget is reset after
 * every attempt (a token is single use), whatever the answer. A failed Turnstile check says CODE_STEP_TURNSTILE_RETRY.
 */
export async function checkSignInCode(
  apiBase: string,
  email: string,
  code: string,
  turnstile: LoginTurnstile | null,
  fetchImpl: typeof fetch = fetch,
): Promise<{ error: string | null; retryAfter?: number; turnstileFailed?: true; session?: SignInSession }> {
  turnstile?.use(STAFF_VERIFY_ACTION);
  const token = turnstile ? await turnstile.token() : '';
  const result = await redeemSignIn(
    apiBase,
    { email, code, ...(token ? { turnstile_token: token } : {}) },
    CODE_REFUSED,
    fetchImpl,
  );
  turnstile?.reset();
  return result.turnstileFailed ? { ...result, error: CODE_STEP_TURNSTILE_RETRY } : result;
}

// ── The login page's holds ──

/** Whole seconds left of a wait that ends at `untilMs`, rounded up; 0 once it is over. */
export function secondsLeft(untilMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((untilMs - nowMs) / 1000));
}

export interface LoginState {
  /** Seconds left of a wait on asking for a code (Send, and "send a new code"). */
  requestWait: number;
  /** Seconds left of a wait on checking a code (Sign in). */
  verifyWait: number;
  /** Seconds left of the 60 second resend cooldown. */
  resendCooldown: number;
  submitting: boolean;
  checking: boolean;
  /** The email box has something in it. */
  hasEmail: boolean;
  /** The code box holds a whole code. */
  codeComplete: boolean;
}

export interface LoginHolds {
  send: { disabled: boolean; label: string };
  signIn: { disabled: boolean; label: string };
  /** "send a new code": offered, or held with how long until it is ("1 minute"). */
  resend: { offered: true } | { offered: false; inText: string };
}

/**
 * Which login buttons a wait holds, and what they say. Asking for a code and checking one are limited separately, so
 * each button waits out its own; "send a new code" waits out the longer of the resend cooldown and the request wait.
 * Sign in also waits while a new code is being asked for: a check then would switch the Turnstile widget under the
 * request (which would go without a token) or check the old code against the new one (and spend one of its tries).
 */
export function loginHolds(s: LoginState): LoginHolds {
  const resendIn = Math.max(s.resendCooldown, s.requestWait);
  return {
    send: {
      disabled: s.submitting || !s.hasEmail || s.requestWait > 0,
      label: s.submitting
        ? 'Sending…'
        : s.requestWait > 0
          ? `Try again in ${signInWaitText(s.requestWait)}`
          : 'Send sign-in code',
    },
    signIn: {
      disabled: s.checking || s.submitting || !s.codeComplete || s.verifyWait > 0,
      label: s.checking
        ? 'Signing in…'
        : s.verifyWait > 0
          ? `Try again in ${signInWaitText(s.verifyWait)}`
          : 'Sign in',
    },
    resend:
      resendIn === 0 ? { offered: true } : { offered: false, inText: signInWaitText(resendIn) },
  };
}

/** The message on screen is a wait message and both waits are over: it goes. */
export function waitMessageOver(errorIsWait: boolean, requestWait: number, verifyWait: number): boolean {
  return errorIsWait && requestWait === 0 && verifyWait === 0;
}
